//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { RiCarLine } from "react-icons/ri";
import Dot from "../components/layout/Dot";
import { IServerRequestInfo } from "../stores";
import { distanceInWordsStrict as timeAgo } from "date-fns";
import Pluralize from "pluralize";
import {
  PENDING_RESPONSE,
  ACCEPTED_RESPONSE,
} from "../stores/jobs/utils/request";
import PriceDisplay from "../components/payments/PriceDisplay";
import { addDays, capFirstLetter } from "../utils/functions";
import Countdown from "../components/data-display/Countdown";
import OldIcon from "../components/ui-elements/Icon";

const getAdditionalSubjectsInfo = (length) => {
  let finalLength = length - 1;
  let details = "";
  if (finalLength > 0) {
    details = `- Plus ${finalLength} more ${Pluralize("subject", finalLength)}`;
  }
  return details;
};

const getJobSummaryTitle = ({
  numberOfStudents,
  lessonType,
  lessonPurpose,
  location,
}: {
  numberOfStudents;
  lessonType: string;
  lessonPurpose: string | Array<string>;
  location: string;
}) => {
  let summary = "";
  let _lessonType =
    typeof lessonType === "string"
      ? capFirstLetter(lessonType)
      : capFirstLetter(lessonType[0]);

  if (numberOfStudents > 1) {
    if (_lessonType === "Online") {
      summary = `Booking requests for ${numberOfStudents} students (${_lessonType})`;
    } else {
      summary = `Booking requests for ${numberOfStudents} students in ${location}`;
    }
  } else {
    if (_lessonType === "Online") {
      summary = `${lessonPurpose} lessons (${_lessonType})`;
    } else {
      summary = `${lessonPurpose} lessons in ${location}`;
    }
  }

  return summary;
};

const getStudentDetails = (students) => {
  const student = students[0];
  const remainingStudents = students.length - 1;
  const purpose = student.classDetail.purpose;
  const firstSubject = student.classDetail.subjects[0];
  const expectation = student.expectation;
  const purposeSummary =
    students.length > 1
      ? ` - Plus ${remainingStudents} more ${Pluralize(
          "purpose",
          remainingStudents,
        )}`
      : "";
  return { student, purposeSummary, purpose, firstSubject, expectation };
};

type Props = {
  tutorResponse: any;
  dateCreated: any;
  tutorInfo: any;
  bookingStage: any;
  customerInfo: any;
  students: Array<any>;
  lessonInfo: any;
  store: IServerRequestInfo;
  billingInfo: any;
  onClick: () => any;
  [key: string]: any;
};

const JobSummary: React.FC<Props> = observer(
  ({
    tutorResponse,
    dateCreated,
    tutorInfo = {},
    bookingStage = {},
    customerInfo: client = {},
    students = [],
    lessonInfo = {},
    store,
    billingInfo = {},
    onClick,
    ...rest
  }) => {
    const finalTakeHomePay =
      billingInfo.tutorEarnings - billingInfo.firstBookingDiscount;
    const clientName = `${client.title} ${client.firstName}`;
    const clientLocation =
      lessonInfo.lessonType === "online" ? `, ${client.state}` : "";
    const timeDifference = timeAgo(new Date(dateCreated), new Date());
    const lessonsCount =
      lessonInfo.lessonDuration * lessonInfo.lessonDays.length;
    const { student, purposeSummary, purpose, firstSubject, expectation } =
      getStudentDetails(students);
    const moreSubjectsInfo = getAdditionalSubjectsInfo(
      student.classDetail.subjects.length,
    );
    const summary = getJobSummaryTitle({
      numberOfStudents: students.length,
      lessonType: lessonInfo.lessonType,
      lessonPurpose: student.classDetail.purpose,
      location: `${client.vicinity}, ${client.region}`,
    });
    const lessonCyle =
      lessonInfo.lessonDuration === 4
        ? "/ month"
        : `/ ${Pluralize("wk", lessonInfo.lessonDuration, true)}`;
    const disableButton = ![ACCEPTED_RESPONSE, PENDING_RESPONSE].includes(
      tutorResponse.status,
    );
    // const status = getRequestStatus(tutorResponse.status, requestStatus);
    const stopDate = addDays(dateCreated, 1);

    // console.log(tutorResponse.status);
    return (
      <Box p={[6, 8]} rounded="lg" borderWidth="2xs" borderColor="neutral.500">
        <Stack spacing={5}>
          {tutorResponse.status === PENDING_RESPONSE && (
            <Stack isInline align="center">
              <OldIcon name="alarm-clock" size={5} color="red.500" />
              <Text fontSize="xs" color="red.500">
                <Countdown date={stopDate} variant="plain" /> left - respond
                quickly!
              </Text>
            </Stack>
          )}

          <Box width={["100%", "80%"]}>
            <Text
              onClick={onClick}
              cursor="pointer"
              fontWeight="bold"
              mb={2}
              lineHeight="short"
              fontSize="xl"
            >
              {summary}
            </Text>
            <HStack flexWrap="wrap">
              <Pill>{students.length > 1 ? purpose : firstSubject}</Pill>
              <Text opacity={0.7} fontSize="xs">
                {students.length > 1 ? purposeSummary : moreSubjectsInfo}
              </Text>
            </HStack>
          </Box>

          <Box width={["100%", "80%"]}>
            <Text mb={2} noOfLines={2} fontSize="sm">
              {expectation}
            </Text>
            <HStack fontSize="sm">
              <Avatar size="xs" src={client.photo} name={clientName.trim()} />
              <HStack>
                <Text>{`${clientName}${clientLocation}`}</Text>
                <Dot />
                <Text>{timeDifference} ago</Text>
              </HStack>
            </HStack>
          </Box>

          <HStack pt={3} pb={1} justifyContent="space-between">
            <Box>
              <HStack align="center">
                <PriceDisplay
                  fontSize={["lg", "xl"]}
                  fontWeight="bold"
                  price={finalTakeHomePay}
                  currency={billingInfo.currency}
                  priceIsFetched={billingInfo.priceIsFetched}
                />
                <Text as="span" fontWeight="normal" fontSize="sm">
                  {lessonCyle}
                </Text>
                {billingInfo.transportFare > 0 ? (
                  <Tooltip
                    hasArrow
                    placement="top"
                    px={4}
                    py={3}
                    label="Includes additional transport stipend"
                    bg="gray.600"
                    color="white"
                    fontSize="sm"
                    rounded="md"
                  >
                    <Icon as={RiCarLine} fontSize="12px" />
                  </Tooltip>
                ) : null}
              </HStack>
              <HStack fontSize="sm" color="gray.600">
                <Text>{`${lessonsCount} lessons`}</Text>
                <Dot />
                <Text>{`${lessonInfo.lessonHours} ${
                  lessonInfo.lessonHours > 1 ? `hours` : `hour`
                }`}</Text>
              </HStack>
            </Box>
            <Button
              onClick={onClick}
              colorScheme="blue"
              isDisabled={disableButton}
            >
              View job
            </Button>
          </HStack>
        </Stack>
      </Box>
    );
  },
);

export default JobSummary;

const Pill = ({ ...rest }) => {
  return (
    <Box
      px="2"
      py="2px"
      fontSize="xs"
      opacity={0.7}
      bg="neutral.200"
      rounded="md"
      {...rest}
    />
  );
};
